import { useCallback, useContext, useMemo } from "react";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress } from "@mui/material";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { DetailsReportEditionContext } from "contexts/DetailsReportEditionContext/DetailsReportEditionContext";
import { DropdownOptions, IOption } from "../Dropdown";
import { useTranslation } from "react-i18next";
import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { handleNameTranslation } from "utilities/localesUtilities";

export const FunctionalLocationsDropdown = ({ row }: { row: IEventReportDetails }) => {
    const { user_id } = useContext(UserContext);
    const { edit, setEdit } = useContext(DetailsReportEditionContext);
    const { t, i18n } = useTranslation();

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFailureTypeIdIsNotSelected = useMemo(
        () =>
            row.failure_type.failure_type_id === edit[existingEdition]?.changes?.failure_type_id &&
            row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
        [row, existingEdition, edit],
    );
    const selectedFailureTypeId = useMemo(
        () => (selectedFailureTypeIdIsNotSelected ? undefined : edit[existingEdition]?.changes?.failure_type_id),
        [existingEdition, edit, selectedFailureTypeIdIsNotSelected],
    );
    const selectedAreaId = useMemo(() => edit[existingEdition]?.changes?.area_id, [edit, existingEdition]);
    const selectedFunctionalLocationId = useMemo(
        () => edit[existingEdition]?.changes?.functional_location_id,
        [edit, existingEdition],
    );
    const { loading, functionalLocations } = EventReportsEntity.GetFunctionalLocations(
        selectedAreaId,
        selectedFailureTypeId,
    );

    const functionalLocationOptions: IOption[] = useMemo(
        () =>
            functionalLocations.map((fl) => ({
                name: handleNameTranslation(i18n.language, fl.translation.translation_names),
                id: fl.functional_location_id,
            })),
        [functionalLocations, i18n.language],
    );

    const currentSelectionNotInOptions = useMemo(
        () => functionalLocationOptions.some((option) => option.id === selectedFunctionalLocationId),
        [selectedFunctionalLocationId, functionalLocationOptions],
    );

    const handleFunctionalLocationUpdate = useCallback(
        async (id: number) => {
            if (existingEdition !== -1) {
                setEdit((prevEdit) => {
                    const newEdit = [...prevEdit];
                    newEdit[existingEdition] = {
                        ...newEdit[existingEdition],
                        changes: {
                            ...newEdit[existingEdition]?.changes,
                            functional_location_id: id,
                            failure_code_id: undefined,
                            ...(user_id && { edited_by_user_id: user_id }),
                        },
                    };
                    return newEdit;
                });
            }
        },
        [existingEdition, setEdit, user_id],
    );

    return loading ? (
        <CircularProgress size={20} color="info" />
    ) : selectedFailureTypeId && edit[existingEdition]?.location_specific ? (
        <DropdownOptions
            obligatory
            disabled={!selectedAreaId}
            pending={!selectedFunctionalLocationId || !currentSelectionNotInOptions}
            inlineStyle={{ width: "80px" }}
            optionSelected={selectedFunctionalLocationId}
            options={functionalLocationOptions}
            handleUpdate={handleFunctionalLocationUpdate}
        />
    ) : (
        <span>{t("locations.mill_dashboards.details.not_applicable")}</span>
    );
};
